import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Contact, ContactKind, Conversation } from '../lib/database.types';

// Single line of paste-in CSV: email[,name[,company]]. Header row optional.
// Returns null for malformed rows (missing email).
function parseManualLine(line: string): { email: string; name: string | null; company: string | null } | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  // Split on comma OR tab so Excel/Sheets pastes work too
  const parts = trimmed.split(/[\t,]/).map((p) => p.trim());
  const email = parts[0]?.toLowerCase();
  if (!email || !email.includes('@')) return null;
  return {
    email,
    name: parts[1] || null,
    company: parts[2] || null,
  };
}

interface ImportResult {
  ok: number;
  failed: number;
  errors: string[];
}

async function importContactsFromText(text: string): Promise<ImportResult> {
  const lines = text.split(/\r?\n/);
  // Skip a header row if its first cell looks like a column name
  const startIdx = /^email/i.test(lines[0] ?? '') ? 1 : 0;
  const result: ImportResult = { ok: 0, failed: 0, errors: [] };

  // Build the known-domains map once from tb_clients (matches what the worker
  // does in loadKnownDomains) so manual imports get the same classification
  // benefit as Gmail/Pipedrive sync.
  const { data: clientRows } = await supabase
    .from('tb_clients')
    .select('slug, context');
  const knownDomains: Record<string, string> = {};
  for (const c of (clientRows ?? []) as Array<{ slug: string; context: { domains?: string[] } }>) {
    for (const d of c.context?.domains ?? []) {
      knownDomains[d.toLowerCase()] = c.slug;
    }
  }

  for (let i = startIdx; i < lines.length; i++) {
    const parsed = parseManualLine(lines[i]);
    if (!parsed) continue;
    const { error } = await supabase.rpc('fn_upsert_contact_signal', {
      p_email: parsed.email,
      p_name: parsed.name,
      p_company: parsed.company,
      p_pipedrive_id: null,
      p_source: 'manual',
      p_stages: [],
      p_labels: [],
      p_last_seen: new Date().toISOString(),
      p_known_domains: knownDomains,
    });
    if (error) {
      result.failed++;
      if (result.errors.length < 5) {
        result.errors.push(`row ${i + 1} (${parsed.email}): ${error.message}`);
      }
    } else {
      result.ok++;
    }
  }
  return result;
}

const KIND_COLORS: Record<ContactKind, string> = {
  client: 'chip-medium',   // blue-filled (we're getting paid)
  lead: 'chip-high',       // amber-filled (work in progress)
  partner: 'chip-low',     // muted (collaborator, no direct transaction)
  unknown: 'chip-low',
};

type KindFilter = 'all' | ContactKind;

// Contacts view. Three primary filters: CLIENT / LEAD / PARTNER.
// Reads tb_contacts + tb_conversations directly. When the contacts-sync
// Worker is deployed, this view gets live data via realtime subscription.
export function Contacts() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<KindFilter>('all');
  const [query, setQuery] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const { data, error } = await supabase
        .from('tb_contacts')
        .select('*')
        .order('last_seen_at', { ascending: false });
      if (!mounted) return;
      if (error) {
        setContacts([]);
      } else {
        setContacts((data as Contact[] | null) ?? []);
      }
      setLoading(false);
    };
    load();

    const sub = supabase
      .channel('contacts-sync')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tb_contacts' },
        (payload) => {
          setContacts((prev) => {
            if (payload.eventType === 'DELETE') {
              return prev.filter((c) => c.id !== (payload.old as Contact).id);
            }
            const next = payload.new as Contact;
            const exists = prev.some((c) => c.id === next.id);
            return exists
              ? prev.map((c) => (c.id === next.id ? next : c))
              : [next, ...prev];
          });
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      sub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!selectedContactId) {
      setConversations([]);
      return;
    }
    let mounted = true;
    supabase
      .from('tb_conversations')
      .select('*')
      .eq('contact_id', selectedContactId)
      .order('occurred_at', { ascending: false })
      .limit(20)
      .then(({ data }) => {
        if (mounted) setConversations((data as Conversation[] | null) ?? []);
      });
    return () => {
      mounted = false;
    };
  }, [selectedContactId]);

  const counts = useMemo(() => {
    return {
      all: contacts.length,
      client: contacts.filter((c) => c.kind === 'client').length,
      lead: contacts.filter((c) => c.kind === 'lead').length,
      partner: contacts.filter((c) => c.kind === 'partner').length,
      unknown: contacts.filter((c) => c.kind === 'unknown').length,
    };
  }, [contacts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return contacts.filter((c) => {
      if (filter !== 'all' && c.kind !== filter) return false;
      if (q) {
        const hay = `${c.name ?? ''} ${c.email ?? ''} ${c.company ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [contacts, filter, query]);

  const selected = selectedContactId
    ? contacts.find((c) => c.id === selectedContactId) ?? null
    : null;

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-5xl p-8 space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight">Contacts</h1>
            <p className="text-sm text-muted font-mono">
              {contacts.length} total · pipedrive · gmail · manual
            </p>
          </div>
          <button
            onClick={() => setImportOpen((v) => !v)}
            className="btn text-xs whitespace-nowrap"
          >
            {importOpen ? 'cancel import' : 'import contacts'}
          </button>
        </header>

        {importOpen && (
          <div className="panel p-4 space-y-3 border-amber/40">
            <div className="text-xs font-mono text-muted">
              Paste one contact per line: <code className="text-amberHi">email,name,company</code>.
              Tab-separated works too (Excel/Sheets paste). Header row optional. Each row goes
              through the atomic upsert RPC with source=manual.
            </div>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={'rick@ipro.com,Rick Wong,iPro Career\nanthony@reputationresolutions.com,Anthony,Reputation Resolutions'}
              className="w-full h-40 p-2 bg-bg border border-border rounded font-mono text-xs focus:outline-none focus:border-amber/60"
              spellCheck={false}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  setImporting(true);
                  setImportResult(null);
                  try {
                    const result = await importContactsFromText(importText);
                    setImportResult(result);
                    if (result.failed === 0 && result.ok > 0) {
                      setImportText('');
                    }
                  } finally {
                    setImporting(false);
                  }
                }}
                disabled={importing || !importText.trim()}
                className="btn text-xs"
              >
                {importing ? 'importing…' : 'run import'}
              </button>
              {importResult && (
                <span className="text-xs font-mono">
                  <span className="text-green">{importResult.ok} ok</span>
                  {importResult.failed > 0 && (
                    <span className="text-red ml-2">{importResult.failed} failed</span>
                  )}
                </span>
              )}
            </div>
            {importResult && importResult.errors.length > 0 && (
              <div className="text-xs font-mono text-red space-y-1">
                {importResult.errors.map((e, i) => (
                  <div key={i}>{e}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {contacts.length === 0 && !loading && !importOpen && (
          <div className="panel p-6 space-y-2 border-amber/30">
            <div className="text-sm font-semibold text-amberHi">
              No contacts yet
            </div>
            <p className="text-xs text-muted font-mono">
              Three ways to get data in: (1) deploy the contacts-sync Cloudflare Worker
              with Gmail/Pipedrive OAuth credentials, (2) use the import button above to
              paste a CSV/TSV manually, (3) call <code className="text-amberHi">fn_upsert_contact_signal</code> via SQL.
            </p>
          </div>
        )}

        {/* Filter chips — the 3 dimensions Andrew cares about */}
        <div className="flex items-center gap-2 flex-wrap">
          <FilterChip
            active={filter === 'all'}
            onClick={() => setFilter('all')}
            label="all"
            count={counts.all}
          />
          <FilterChip
            active={filter === 'client'}
            onClick={() => setFilter('client')}
            label="CLIENT"
            count={counts.client}
            cls="chip-medium"
          />
          <FilterChip
            active={filter === 'lead'}
            onClick={() => setFilter('lead')}
            label="LEAD"
            count={counts.lead}
            cls="chip-high"
          />
          <FilterChip
            active={filter === 'partner'}
            onClick={() => setFilter('partner')}
            label="PARTNER"
            count={counts.partner}
            cls="chip-low"
          />
          {counts.unknown > 0 && (
            <FilterChip
              active={filter === 'unknown'}
              onClick={() => setFilter('unknown')}
              label="unknown"
              count={counts.unknown}
            />
          )}

          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="search name, email, company…"
            className="ml-auto px-3 py-2 bg-panelHi border border-border rounded-md text-sm placeholder-dim focus:outline-none focus:border-amber/60 min-w-[260px]"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Contact list */}
          <div className="lg:col-span-3 space-y-1.5">
            {filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedContactId(c.id)}
                className={
                  'panel w-full p-3 text-left flex items-center gap-3 transition-colors ' +
                  (selectedContactId === c.id
                    ? 'border-amber/60 bg-panelHi/40'
                    : 'hover:border-borderHi')
                }
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {c.name ?? c.email ?? '(unknown)'}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted font-mono mt-1 flex-wrap">
                    {c.company && <span>{c.company}</span>}
                    {c.company && c.email && <span className="text-dim">·</span>}
                    {c.email && <span className="truncate">{c.email}</span>}
                  </div>
                </div>
                <span className={`chip ${KIND_COLORS[c.kind]} shrink-0`}>
                  {c.kind.toUpperCase()}
                </span>
              </button>
            ))}
            {!loading && filtered.length === 0 && (
              <div className="panel p-8 text-center text-dim font-mono text-sm">
                no contacts match
              </div>
            )}
          </div>

          {/* Contact detail + conversations */}
          <div className="lg:col-span-2">
            {selected ? (
              <div className="panel p-4 space-y-4 sticky top-4">
                <div>
                  <div className="text-xs font-mono text-dim uppercase tracking-widest">
                    {selected.kind}
                  </div>
                  <div className="text-lg font-semibold mt-1">{selected.name ?? 'Unknown'}</div>
                  {selected.email && (
                    <div className="text-xs font-mono text-muted mt-0.5">{selected.email}</div>
                  )}
                </div>
                {selected.company && (
                  <div>
                    <div className="text-[10px] font-mono text-dim uppercase tracking-widest">
                      Company
                    </div>
                    <div className="text-sm">{selected.company}</div>
                  </div>
                )}
                {selected.sources.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono text-dim uppercase tracking-widest">
                      Sources
                    </div>
                    <div className="flex gap-1 mt-1">
                      {selected.sources.map((s) => (
                        <span key={s} className="chip chip-low text-[10px]">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] font-mono text-dim uppercase tracking-widest mb-2">
                    Recent conversations ({conversations.length})
                  </div>
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                    {conversations.map((conv) => (
                      <div key={conv.id} className="panel-hi p-2 rounded space-y-1">
                        <div className="flex items-baseline justify-between text-[10px] font-mono text-dim">
                          <span className="text-amberHi">{conv.source}</span>
                          <span>{new Date(conv.occurred_at).toLocaleDateString()}</span>
                        </div>
                        {conv.subject && (
                          <div className="text-xs font-medium truncate">{conv.subject}</div>
                        )}
                        {conv.body && (
                          <div className="text-[11px] text-muted line-clamp-2 font-mono">
                            {conv.body}
                          </div>
                        )}
                      </div>
                    ))}
                    {conversations.length === 0 && (
                      <div className="text-[11px] text-dim italic">no conversations yet</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="panel p-6 text-center text-dim font-mono text-xs">
                pick a contact
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
  cls,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  cls?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'px-3 py-2 rounded-md text-xs font-mono min-h-[36px] transition-colors border flex items-center gap-2 ' +
        (active
          ? (cls ? `chip ${cls} !font-bold` : 'bg-amber border-amber text-bg font-semibold')
          : 'bg-panelHi border-border text-muted hover:border-borderHi')
      }
    >
      <span>{label}</span>
      <span className="tabular-nums opacity-70">{count}</span>
    </button>
  );
}
